import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '@/lib/api';
import type { MarketingLocale } from './guides';
import { marketingCopy } from './copy';
import { sampleRequestCopy } from './sampleRequestCopy';
import { networkFailureKind, reportBrowserFailure } from '@/monitoring/browserErrors';

type Turnstile = {
  render(element: HTMLElement, options: Record<string, unknown>): string;
  remove(widget: string): void;
  reset(widget: string): void;
};
declare global { interface Window { turnstile?: Turnstile } }
let loading: Promise<Turnstile> | undefined;

function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loading) loading = new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('verification_unavailable'));
    script.onerror = () => { script.remove(); reject(new Error('verification_unavailable')); };
    document.head.appendChild(script);
  }).catch(error => { loading = undefined; throw error; });
  return loading;
}

export type InquiryResult = 'accepted' | 'failed' | 'unknown';
export async function sendInquiry(payload: Record<string, string>, request: typeof fetch = fetch): Promise<InquiryResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 45000);
  try {
    const response = await request(`${api.baseUrl}/api/marketing/inquiries`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      signal: controller.signal,
    });
    let body;
    try { body = await response.json(); }
    catch {
      reportBrowserFailure({ stage: 'sample_submit', kind: controller.signal.aborted ? 'timeout' : 'invalid_response', status: response.status });
      return 'unknown';
    }
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      reportBrowserFailure({ stage: 'sample_submit', kind: 'invalid_response', status: response.status });
      return 'unknown';
    }
    if (response.ok && body.status === 'accepted') return 'accepted';
    if (response.status >= 500 || response.status === 429 || response.ok) {
      const kind = body.detail?.code === 'delivery_unknown' ? 'delivery_unknown'
        : body.detail?.code === 'delivery_failed' ? 'delivery_failed' : response.ok ? 'invalid_response' : 'http';
      reportBrowserFailure({ stage: 'sample_submit', kind, status: response.status,
        requestId: response.headers?.get('X-Request-Id') || undefined });
    }
    if (body.detail?.code === 'delivery_unknown' || response.status >= 500 && !body.detail?.code) return 'unknown';
    return 'failed';
  } catch (error) {
    reportBrowserFailure({ stage: 'sample_submit', kind: controller.signal.aborted ? 'timeout' : networkFailureKind(error) });
    return 'unknown';
  } finally { clearTimeout(timeout); }
}

export default function InquiryForm({ locale, kind = 'guided', onResult, onEmail, siteKey = import.meta.env.VITE_MARKETING_TURNSTILE_SITE_KEY }: {
  locale: MarketingLocale; kind?: 'guided' | 'extension'; onResult?: (result: InquiryResult) => void; onEmail?: () => void; siteKey?: string;
}) {
  const sample = sampleRequestCopy(locale);
  const isSample = kind === 'guided';
  const copy = { ...marketingCopy(locale), ...(isSample ? sample : {}) };
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string>();
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | InquiryResult>('idle');
  const [unavailable, setUnavailable] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    if (!siteKey) return;
    let canceled = false;
    void loadTurnstile().then(turnstile => {
      if (canceled || !container.current) return;
      widget.current = turnstile.render(container.current, {
        sitekey: siteKey, action: 'inquiry', language: locale === 'en' ? 'en' : locale === 'zh-Hant' ? 'zh-tw' : 'zh-cn',
        theme: 'light', callback: (value: string) => { setToken(value); setUnavailable(false); },
        'expired-callback': () => setToken(''),
        'error-callback': () => {
          setToken(''); setUnavailable(true);
          reportBrowserFailure({ stage: 'turnstile_verification', kind: 'verification' });
        },
      });
    }).catch(() => {
      if (!canceled) {
        setUnavailable(true);
        reportBrowserFailure({ stage: 'turnstile_load', kind: 'load' });
      }
    });
    return () => {
      canceled = true;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = undefined;
    };
  }, [siteKey, locale]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!siteKey || !token || submitting.current || status === 'unknown' || status === 'accepted') return;
    const fields = new FormData(event.currentTarget);
    const payload = Object.fromEntries(['email', 'business', 'market', 'goal', 'website'].map(key => [key, String(fields.get(key) ?? '').trim()]));
    if (isSample) payload.goal = `${sample.requestGoal}${payload.goal ? `\n\n${payload.goal}` : ''}`;
    submitting.current = true; setStatus('sending');
    const result = await sendInquiry({ ...payload, kind, locale, token });
    setToken(''); setStatus(result); submitting.current = false;
    if (widget.current && result === 'failed') window.turnstile?.reset(widget.current);
    onResult?.(result);
  }

  return <section id="inquiry" className="ms-inquiry ph-no-capture" aria-labelledby="inquiry-title">
    <div><p className="ms-eyebrow">{isSample ? 'YOUR FIRST SAMPLE' : 'LET’S TALK'}</p><h2 id="inquiry-title">{isSample ? sample.formTitle : copy.serviceCta}</h2><p>{isSample ? sample.formIntro : copy.serviceNote}</p>
      {isSample && <div className="ms-sample-next"><h3>{sample.whatNext}</h3><ol>{sample.nextSteps.map(step => <li key={step}>{step}</li>)}</ol></div>}
      <a className="ms-text-link ms-form-email" href="mailto:mark.ma@mindstreamapp.ai" aria-label={`${sample.emailAlternative} mark.ma@mindstreamapp.ai`} onClick={onEmail}>{isSample ? sample.emailAlternative : 'mark.ma@mindstreamapp.ai'} ↗</a>
    </div>
    <form onSubmit={submit} className="ms-form" aria-label={isSample ? sample.formTitle : copy.serviceCta}>
        {status === 'accepted' ? <p role="status" className="ms-notice">{copy.sent}</p> : <>
          <label>{copy.email}<input name="email" type="email" autoComplete="email" required maxLength={254} /></label>
          <label>{copy.business}<input name="business" required maxLength={300} placeholder={isSample ? sample.businessPlaceholder : undefined} /></label>
          <label>{copy.market}<input name="market" required maxLength={300} placeholder={isSample ? sample.marketPlaceholder : undefined} /></label>
          {isSample ? <details className="ms-form-optional"><summary>{sample.optional}</summary><label>{copy.goal}<textarea name="goal" maxLength={1600} rows={3} /></label></details>
            : <label>{copy.goal}<textarea name="goal" required maxLength={2000} rows={4} /></label>}
          <div className="ms-honeypot" aria-hidden="true"><label>Website<input name="website" tabIndex={-1} autoComplete="off" /></label></div>
          <div ref={container} />
          <p className="ms-fine">{copy.formPrivacy} <a href="/privacy/">{copy.privacy}</a></p>
          {!siteKey && <p className="ms-form-preview">{copy.formUnavailable}</p>}
          {(unavailable || status === 'failed' || status === 'unknown') && <p role="alert">{copy.failed}</p>}
          <button className="ms-button" type="submit" disabled={!siteKey || !token || status === 'sending' || status === 'unknown' || unavailable}>
            {status === 'sending' ? copy.sending : copy.send}
          </button>
        </>}
      </form>
  </section>;
}
